/* Libraries */
import React, { useState, useEffect } from "react";
import { Form, Label, Button, Icon } from "semantic-ui-react";
import { isEmpty, isArray, get, map } from "lodash";
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Modals
import ContactsFilter from "./Modals/ContactsFilter";
import MultipleView from "./Modals/MultipleView";

// Components
import Input from "../Input";

// Styles
import styles from "./styles.module.css";

const InputFilter = ({
	selectedContact,
	empty,
	disabled,
	searchOnly,
	label,
	multiple,
	onEmpty,
	contactData,
	defaultField = "name",
	url = "",
	tableColumns = [],
	urlParams = {},
	selectTitle = "Seleccionar",
	forceInputValue = "",
	isWhatsapp,
	onAddButtonClick,
	includePhone = false,
	includeEmail = false,
	inputClassName,
	cardLine1 = [],
	cardLine2 = [],
	mobileCardView = false,
}) => {
	// States
	const [contactName, setContactName] = useState("");
	const [errors, setErrors] = useState("");
	const [field, setField] = useState("");

	// modalFilter
	const [openContactsFilter, setOpenContactsFilter] = useState(false);
	const [contactValue, setContactValue] = useState([]);
	const [contactFilterOption, setContactFilterOption] = useState([]);
	const [openBool, setOpenBool] = useState(false);

	// MultipleView
	const [openMultipleView, setOpenMultipleView] = useState(false);

	// useEffects
	useEffect(() => {
		setField(defaultField);
	}, []);

	useEffect(() => {
		let mapOptions = [];
		if (!isEmpty(tableColumns)) {
			mapOptions = map(tableColumns, (column, index) => {
				return { key: index, text: column.label, value: column.key };
			});
		}
		// Solo mostrar "Todo" cuando el input esté vacío
		const options = isEmpty(contactName) ? [{ key: "todo", text: "Todo", value: "all" }, ...mapOptions] : mapOptions;
		setContactFilterOption(options);
		setField(defaultField);
	}, [tableColumns, contactName]);

	useEffect(() => {
		setContactValue([]);
		setContactName("");
	}, [empty]);

	useEffect(() => {
		if (isEmpty(contactData)) {
			setContactValue([]);
			setContactName("");
		} else {
			let currentField = defaultField;

			if (!isEmpty(forceInputValue)) {
				currentField = forceInputValue;
			}
			setContactValue(contactData);
			const extraData = includePhone ? ` (${get(contactData, "phone", "")})` : includeEmail ? ` (${get(contactData, "email", "")})` : "";
			setContactName(`${get(contactData, "id", "")} - ${get(contactData, currentField, "")}${extraData}`);
		}
	}, [contactData]);

	// Functions
	const handleSelectedContact = (item) => {
		let value = [];
		if (multiple) {
			value = [...item, ...contactValue];
		} else if (!isArray(item)) {
			value = item;
		}
		selectedContact(value);
		setContactValue(value);
		const extraData = includePhone ? ` (${get(value, "phone", "")})` : includeEmail ? ` (${get(value, "email", "")})` : "";
		setContactName(`${get(value, "id", "")} - ${get(value, defaultField, "")}${extraData}`);
		setOpenContactsFilter(false);
	};

	const handleRemoveContact = (item) => {
		selectedContact(item);
		setContactValue(item);
		setContactName("");
		setOpenMultipleView(false);
	};

	const handleSearch = () => {
		if (searchOnly) {
			setErrors("");
		} else if (isEmpty(contactValue)) {
			if (!isEmpty(contactName)) {
				filterOpen();
			} else {
				setOpenBool(true);
				setOpenContactsFilter(true);
			}
		} else {
			setOpenContactsFilter(true);
		}
	};

	const filterOpen = () => {
		setErrors("");
		setOpenContactsFilter(true);
	};

	return (
		<div className={styles.contInputContact}>
			<Form.Field className={styles.flexRow}>
				<div className={styles.textAreaContainer}>
					{label && <div className={styles.label}>{label}</div>}
					{!disabled && (
						<Input
							disabled={disabled || (!isEmpty(contactValue) && !multiple)}
							value={contactName}
							className={`${styles.input} ${inputClassName}`}
							fluid
							onKeyDown={(e) => {
								return e.keyCode === 13 && !isEmpty(contactName) && handleSearch();
							}}
							onChange={(e, { value }) => {
								return setContactName(value);
							}}
							dropdown={
								isEmpty(contactName)
									? {
											options: contactFilterOption,
											onChange: (e, data) => {
												return setField(data.value);
											},
											value: field,
									  }
									: undefined
							}
							dropdownPosition="right"
						/>
					)}

					{disabled && !multiple && <Input disabled value={contactName} className={styles.input} fluid icon="user" />}

					<div className={styles.rowButton}>
						{!isEmpty(contactValue) && !disabled && !multiple && (
							<Button
								icon="close"
								onClick={() => {
									setContactName("");
									onEmpty();
									setContactValue([]);
								}}
								className={styles.smallBtnClear}
							/>
						)}
						{!(disabled || (!isEmpty(contactValue) && !multiple)) && (
							<Button
								icon="filter"
								onClick={() => {
									handleSearch();
								}}
								className={styles.smallBtn}
							/>
						)}
						{!isEmpty(contactValue) && multiple && (
							<Button
								onClick={() => {
									setOpenMultipleView(true);
								}}
								className={styles.smallBtn}>
								<Icon name="eye" />
								{` ${contactValue.length} - Seleccionados`}
							</Button>
						)}
						<div />
					</div>

					{openContactsFilter && (
						<ContactsFilter
							openEmpty={openBool}
							omitListId={contactValue}
							multiple={multiple}
							selectedContact={handleSelectedContact}
							query={contactName}
							field={field}
							selectTitle={selectTitle}
							open
							url={url}
							urlParams={urlParams}
							tableColumns={tableColumns}
							contactData={selectedContact}
							onClose={() => {
								setOpenContactsFilter(false);
								setOpenBool(false);
							}}
							isWhatsapp={isWhatsapp}
							onAddButtonClick={onAddButtonClick}
							cardLine1={cardLine1}
							cardLine2={cardLine2}
							mobileCardView={mobileCardView}
						/>
					)}
					{openMultipleView && (
						<MultipleView
							disabled={disabled}
							tableColumns={tableColumns}
							selectedContact={contactValue}
							onRemove={handleRemoveContact}
							multiple={multiple}
							open
							onClose={() => {
								setOpenMultipleView(false);
							}}
						/>
					)}
				</div>
			</Form.Field>
			{!isEmpty(errors) && (
				<Label className={styles.messageErrorTextBox} prompt>
					{errors}
				</Label>
			)}
		</div>
	);
};

InputFilter.propTypes = {
	selectedContact: PropTypes.func,
	onEmpty: PropTypes.func,
	disabled: PropTypes.bool,
	empty: PropTypes.bool,
	contactData: PropTypes.object,
	searchOnly: PropTypes.bool,
	label: PropTypes.string,
	multiple: PropTypes.bool,
	defaultField: PropTypes.string,
	url: PropTypes.string,
	tableColumns: PropTypes.array,
	urlParams: PropTypes.object,
	selectTitle: PropTypes.string,
	forceInputValue: PropTypes.string,
	isWhatsapp: PropTypes.bool,
	onAddButtonClick: PropTypes.func,
	includePhone: PropTypes.bool,
	includeEmail: PropTypes.bool,
	inputClassName: PropTypes.string,
	cardLine1: PropTypes.array,
	cardLine2: PropTypes.array,
	mobileCardView: PropTypes.bool,
	// dispatch: PropTypes.func,
};

InputFilter.defaultProps = {
	disabled: false,
	searchOnly: false,
	empty: false,
	selectTitle: "Seleccionar",
	multiple: false,
	inputClassName: "",

	onEmpty: () => {
		return 0;
	},
	selectedContact: () => {
		return 0;
	},
};

export default connect()(InputFilter);
